'use client';

import styled from 'styled-components';

import type { BlockTemplate, ReviewData } from '@zoomit/editor';
import { Flex, Image } from '@zoomit/primitives';
import { BaseMediaUrl, BlockContainer, isMobile } from '@zoomit/web-kit';
import { cssVar, theme, Typography } from '@zoomit/zoomit-ds';

import { useWebKitCustomization } from '../../../hooks/useWebKitCustomization';

type Props = {
  template: BlockTemplate['review'];
  blockData: ReviewData;
  hasSidebar: boolean;
};

const ReviewBlock = (props: Props) => {
  const { blockData, hasSidebar } = props;
  const { title, description, image, score } = blockData;
  const tenantName = useWebKitCustomization((x) => x.tenantPersianTitle);

  return (
    <>
      <BlockContainer hasSidebar={hasSidebar} template="standard" style={{ margin: 'auto', padding: 0 }}>
        <ImageWrapper>
          <StyledImage
            src={`${BaseMediaUrl}/${image.fileName}-${image.id}`}
            alt={image.alt || ''}
            placeholder="blur"
            blurDataURL={image && image.preview}
            fill
          />
        </ImageWrapper>
      </BlockContainer>
      <BlockContainer hasSidebar={hasSidebar} template="small" style={{ margin: 'auto', padding: 0 }}>
        <BlockHeading
          alignItems={isMobile ? 'center' : 'flex-end'}
          justifyContent="space-between"
          flexDirection={isMobile ? 'column-reverse' : 'row'}
          spacing={{ mb: isMobile ? 'x0' : 'x2', mt: isMobile ? -5 : -7.2 }}
          gap={isMobile ? 1.6 : 0}
        >
          <Typography
            lineHeight="4.8rem"
            variant="h2"
            style={{ alignSelf: isMobile ? 'flex-start' : 'initial' }}
          >
            {title}
          </Typography>
          <ScoreBox
            flexDirection="column"
            alignItems="center"
            justifyContent="flex-end"
            isMobile={isMobile}
            gap={1.4}
            spacing={{ pb: 'x2' }}
          >
            <Flex flexWrap="nowrap" alignItems="center" justifyContent="center" gap={isMobile ? 0.4 : 0.8}>
              <Typography
                fontSize={isMobile ? 2 : 3.2}
                fontWeight={700}
                className="fa"
                textColor={cssVar(theme.white)}
              >
                10
              </Typography>
              <Typography
                fontSize={isMobile ? 2 : 3.2}
                fontWeight={700}
                className="fa"
                textColor={cssVar(theme.white)}
              >
                /
              </Typography>
              <Typography
                fontSize={isMobile ? 3.2 : 4.8}
                fontWeight={700}
                className="fa"
                variant="subhead"
                textColor={cssVar(theme.white)}
              >
                {score}
              </Typography>
            </Flex>
            <Typography variant="subhead" fontWeight={700} textColor={cssVar(theme.white)}>
              امتیاز {tenantName}
            </Typography>
          </ScoreBox>
        </BlockHeading>
        <Typography fontSize={1.6} lineHeight={1.88}>
          {description}
        </Typography>
      </BlockContainer>
    </>
  );
};
export default ReviewBlock;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: var(${theme.aspectRatio.primary});
`;

const ScoreBox = styled(Flex)<{ isMobile: boolean }>`
  width: ${({ isMobile }) => (isMobile ? '12.4rem' : '17rem')};
  height: ${({ isMobile }) => (isMobile ? '8.3rem' : '11rem')};
  position: relative;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    z-index: -1;
    width: 100%;
    height: ${({ isMobile }) => (isMobile ? '5.85rem' : '8rem')};
    background: var(${theme.primary1});
    border-radius: var(${theme.radius.md});
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    bottom: 0;
    z-index: -1;
    width: 100%;
    height: 4.4rem;
    background: var(${theme.black5});
    border-radius: 0 0 var(${theme.radius.md}) var(${theme.radius.md});
  }
`;

const StyledImage = styled(Image)`
  border-radius: var(${theme.radius.sm});
`;

const BlockHeading = styled(Flex)`
  position: relative;
  z-index: 1;
`;
